import { useMemo } from 'react';

import { bandTransition } from './WallRocks';
import { lightState, voidRgba } from '@/game/engine/lighting';
import { project, type Viewport } from '@/game/engine/projection';

/**
 * Everything in the corridor that is not a rock, the miner, or the light:
 * stalactites, floor rubble, and drifting dust.
 *
 * Purpose is legibility, not prettiness. A tunnel of concentric rings with one
 * rock in it gives the eye nothing to measure movement against, so walking felt
 * like the scenery was being swapped rather than travelled through. Decor at
 * varied depths parallaxes past at different rates and turns a step into
 * motion.
 *
 * It is **derived from depth, not stored**: each piece's position comes from a
 * hash of its index and the current 20 m band, so the cave is different at
 * different depths, identical on re-render, and costs no state. Nothing here
 * touches the run — it cannot desync, because there is nothing to sync.
 */

/** Deterministic 0–1 from any pair of integers. */
function noise(a: number, b: number): number {
  let h = (a * 374761393 + b * 668265263) >>> 0;
  h = ((h ^ (h >>> 13)) * 1274126177) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

interface Piece {
  u: number;
  v: number;
  d: number;
  size: number;
  seed: number;
}

/**
 * Lay out one class of decor across the visible depth.
 *
 * `band` changes every 20 m so the cave re-dresses as you descend without
 * anything having to move: the same function, given a new band, simply returns
 * a different arrangement.
 */
function layout(kind: number, band: number, count: number, visible: number): Piece[] {
  return Array.from({ length: count }, (_, i) => {
    const n1 = noise(kind * 1000 + i, band);
    const n2 = noise(kind * 2000 + i, band + 7);
    const n3 = noise(kind * 3000 + i, band + 13);
    return {
      /* Pushed toward the walls: the middle of the corridor is where the rock
         and the miner live, and clutter there competes with the thing the
         player is meant to tap. */
      u: (n1 < 0.5 ? -1 : 1) * (0.55 + n2 * 0.42),
      v: 0,
      d: 1.5 + n3 * Math.max(4, visible - 2),
      size: 0.5 + n2 * 0.9,
      seed: i,
    };
  });
}

/** One band's worth of every decor class. */
interface DecorSet {
  band: number;
  tips: Piece[];
  stumps: Piece[];
  rubble: Piece[];
  motes: Piece[];
}

function buildSet(band: number, visible: number): DecorSet {
  return {
    band,
    tips: layout(1, band, 7, visible),
    /* Stalagmites: the floor's answer to the ceiling's stalactites. Half as
       many — the floor already carries the rubble — so the pair reads as an
       occasional formation, not a picket fence either edge of the corridor. */
    stumps: layout(4, band, 4, visible),
    rubble: layout(2, band, 9, visible),
    motes: layout(3, band, 11, visible),
  };
}

export function CaveDecor({ view, lightPx, depth, torchPct, energyPct, fog }: {
  view: Viewport;
  lightPx: number;
  depth: number;
  torchPct: number;
  energyPct: number;
  /** The cave's own `fog_hex`, so a formation fades into THIS cave's dark, not a
      hardcoded near-black that fights a cold-tinted zone like Glass Reef. */
  fog?: string;
}) {
  const L = lightState({ lightPx, torchPct, energyPct, depth, ...(fog ? { fog } : {}) });
  const visible = Math.max(4, L.radius / (170 / 12));

  /* Opaque — these are gradient endpoints standing in for solid shadow, not a
     translucent overlay, so alpha stays 1 and only the hue moves with the cave. */
  const dark = voidRgba(L.voidRgb, 1);
  const warm = `rgb(${L.warm[0]},${L.warm[1]},${L.warm[2]})`;

  const { current, previous, fadeIn } = useMemo(() => {
    /* Re-dress every 20 m, the same band WallRocks uses, so decor and wall rocks
       change together instead of drifting in and out of step with each other.
       `bandTransition` is the same crossfade window WallRocks softens its own
       band pop with — every stalactite, stalagmite, rubble piece and dust mote
       used to jump position and size on the single frame `depth` crossed a
       multiple of 20, same failure, same fix. */
    const { band, fading, fadeIn } = bandTransition(depth);
    return {
      current: buildSet(band, visible),
      previous: fading ? buildSet(band - 1, visible) : null,
      fadeIn,
    };
  }, [depth, visible]);

  return (
    <div className="pointer-events-none absolute inset-0 z-[3] overflow-hidden" aria-hidden>
      {drawSet(current, fadeIn, visible, view, lightPx, dark, warm)}
      {previous && drawSet(previous, 1 - fadeIn, visible, view, lightPx, dark, warm)}
    </div>
  );
}

/** Every piece of one band's decor, at a given crossfade multiplier. */
function drawSet(
  set: DecorSet, fadeMul: number, visible: number,
  view: Viewport, lightPx: number, dark: string, warm: string,
) {
  if (fadeMul <= 0.002) return null;
  const prefix = `${set.band}`;
  return (
    <>
      {/* ── stalactites: hang from the ceiling ── */}
      {set.tips.map((p) => renderTip(p, `${prefix}t${p.seed}`, fadeMul, view, lightPx, dark))}

      {/* ── stalagmites: the floor's own formations, rising to meet the tips ── */}
      {set.stumps.map((p) => renderStump(p, `${prefix}g${p.seed}`, fadeMul, view, lightPx, dark))}

      {/* ── floor rubble ──
          Two silhouettes, picked per piece from its own noise rather than
          alternating index — a worn blob and an angular shard — so the floor
          does not read as one shape stamped repeatedly at different sizes. */}
      {set.rubble.map((p) => renderRubble(p, `${prefix}r${p.seed}`, set.band, fadeMul, view, lightPx, dark))}

      {/* ── dust in the torchlight ──
          Only near the flame: motes are visible because light catches them, so
          drawing them out in the dark would read as screen dirt. */}
      {set.motes.map((p) =>
        renderMote(p, `${prefix}m${p.seed}`, set.band, fadeMul, visible, view, lightPx, warm))}
    </>
  );
}

function renderTip(p: Piece, key: string, fadeMul: number, view: Viewport, lightPx: number, dark: string) {
  const s = project(p.u * 0.9, 0.92, p.d, view, lightPx);
  if (s.opacity <= 0.04) return null;
  const h = 26 * p.size * s.scale;
  const w = 9 * p.size * s.scale;
  return (
    <div
      key={key}
      className="absolute"
      style={{
        left: s.x, top: s.y, width: w, height: h,
        transform: 'translateX(-50%)',
        opacity: s.opacity * 0.75 * fadeMul,
        zIndex: s.z,
        background: `linear-gradient(180deg, var(--cave-wall-near) 0%, ${dark} 100%)`,
        clipPath: 'polygon(0 0, 100% 0, 50% 100%)',
      }}
    />
  );
}

function renderStump(p: Piece, key: string, fadeMul: number, view: Viewport, lightPx: number, dark: string) {
  const s = project(p.u * 0.85, -0.9, p.d, view, lightPx);
  if (s.opacity <= 0.04) return null;
  const h = 20 * p.size * s.scale;
  const w = 11 * p.size * s.scale;
  return (
    <div
      key={key}
      className="absolute"
      style={{
        left: s.x, top: s.y, width: w, height: h,
        transform: 'translate(-50%, -100%)',
        opacity: s.opacity * 0.75 * fadeMul,
        zIndex: s.z,
        background: `linear-gradient(0deg, var(--cave-wall-near) 0%, ${dark} 100%)`,
        clipPath: 'polygon(0 100%, 100% 100%, 58% 0)',
      }}
    />
  );
}

function renderRubble(
  p: Piece, key: string, band: number, fadeMul: number, view: Viewport, lightPx: number, dark: string,
) {
  const s = project(p.u, -0.86, p.d, view, lightPx);
  if (s.opacity <= 0.04) return null;
  const w = 16 * p.size * s.scale;
  /* `band`, not the outer component's current band — a fading-out piece from the
     previous band must keep the same angular/rounded silhouette it was built
     with, or it would visibly change shape mid-fade. */
  const angular = noise(p.seed, band + 29) > 0.55;
  return (
    <div
      key={key}
      className={angular ? 'absolute' : 'absolute rounded-[45%_55%_50%_50%/60%_50%_50%_40%]'}
      style={{
        left: s.x, top: s.y, width: w, height: w * 0.55,
        transform: 'translate(-50%,-50%)',
        opacity: s.opacity * 0.85 * fadeMul,
        zIndex: s.z,
        background: `linear-gradient(160deg, var(--cave-wall-mid), ${dark})`,
        clipPath: angular ? 'polygon(4% 80%, 30% 20%, 62% 0, 100% 55%, 78% 100%, 20% 100%)' : undefined,
      }}
    />
  );
}

function renderMote(
  p: Piece, key: string, band: number, fadeMul: number, visible: number,
  view: Viewport, lightPx: number, warm: string,
) {
  /* Only near the flame: motes are visible because light catches them, so
     drawing them out in the dark would read as screen dirt. */
  if (p.d > visible * 0.55) return null;
  const s = project(p.u * 0.8, -0.2 + noise(p.seed, band) * 0.9, p.d, view, lightPx);
  if (s.opacity <= 0.1) return null;
  const size = Math.max(1, 2.4 * p.size * s.scale);
  return (
    <div
      key={key}
      className="absolute rounded-full"
      style={{
        left: s.x, top: s.y, width: size, height: size,
        opacity: s.opacity * 0.5 * fadeMul,
        background: warm,
        zIndex: s.z,
        animation: `mote ${5 + (p.seed % 5)}s ease-in-out infinite`,
        animationDelay: `${p.seed * 0.4}s`,
      }}
    />
  );
}
